import React, { useRef, useState, type FC } from "react";
import styled, { css } from "styled-components";
import { FaPlay, FaPause } from "react-icons/fa";
import { theme } from "@src/theme";

const PlayerContainer = styled.div`
  padding: 16px;
  border-radius: 8px;
  width: 320px;
  display: flex;
  align-items: center;
  color: ${theme.colors.yellow};
  font-family: "Kern Standard", Arial, sans-serif;
  position: relative;
`;

const PlayButton = styled.button`
  background: ${theme.colors.yellow};
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  cursor: pointer;
  margin-right: 10px;

  &:focus {
    outline: none;
  }
`;

const ProgressBarContainer = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  position: relative;
`;

const trackH = "0.4em";
const thumbD = "0.4em";
const trackC = theme.colors.gray300;
const filllC = theme.colors.yellow;

const track = css`
  box-sizing: border-box;
  border: none;
  height: 4px;
  background: ${trackC};
`;

const trackFill = css`
  ${track};
  height: 6px;
  background-color: transparent;
  background-image:
    linear-gradient(${filllC}, ${filllC}), linear-gradient(${trackC}, ${trackC});
  background-size:
    var(--sx) 6px,
    calc(100% - var(--sx)) 4px;
  background-position:
    left center,
    right center;
  background-repeat: no-repeat;
`;

const fill = css`
  height: ${trackH};
  background: ${filllC};
`;

const thumb = css`
  box-sizing: border-box;
  border: none;
  width: ${thumbD};
  height: ${thumbD};
  background: ${theme.colors.yellow};
  outline: none;
`;

const SliderInput = styled.input`
  &,
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
  }

  &:focus {
    outline: none;
  }

  &:focus::-webkit-slider-thumb {
    outline: none;
  }

  &:focus::-moz-range-thumb {
    outline: none;
  }

  &:focus::-ms-thumb {
    outline: none;
  }

  --range: calc(var(--max) - var(--min));
  --ratio: calc((var(--val) - var(--min)) / var(--range));
  --sx: calc(0.5 * ${thumbD} + var(--ratio) * (100% - ${thumbD}));

  margin: 0;
  padding: 0;
  height: ${thumbD};
  background: transparent;
  font:
    1em/1 arial,
    sans-serif;

  &::-webkit-slider-runnable-track {
    ${trackFill};
  }

  &::-moz-range-track {
    ${track};
  }

  &::-ms-track {
    ${track};
  }

  &::-moz-range-progress {
    ${fill};
  }

  &::-ms-fill-lower {
    ${fill};
  }

  &::-webkit-slider-thumb {
    margin-top: calc(0.5 * (${trackH} - ${thumbD}));
    ${thumb};
  }

  &::-moz-range-thumb {
    ${thumb};
  }

  &::-ms-thumb {
    margin-top: 0;
    ${thumb};
  }

  &::-ms-tooltip {
    display: none;
  }

  &::-moz-focus-outer {
    border: 0;
  }
`;
const PauseIcon = FaPause as unknown as FC;
const PlayIcon = FaPlay as unknown as FC;

const TrackPlayer = ({
  audioUrl,
  hideSlider = true,
}: {
  audioUrl: string;
  hideSlider?: boolean;
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const percentage =
        (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(percentage || 0);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      const newTime =
        (parseFloat(e.target.value) / 100) * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
      setProgress(parseFloat(e.target.value));
    }
  };

  return (
    <PlayerContainer
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <PlayButton
        onClick={(e) => {
          e.stopPropagation();
          togglePlay();
        }}
      >
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </PlayButton>
      <ProgressBarContainer
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {!hideSlider && (
          <SliderInput
            onClick={(e) => {
              e.stopPropagation();
            }}
            type="range"
            min="0"
            max="100"
            value={progress}
            onChange={handleSeek}
            style={
              {
                width: "100%",
                "--min": 0,
                "--max": 100,
                "--val": progress,
              } as React.CSSProperties
            }
          />
        )}
      </ProgressBarContainer>
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
      />
    </PlayerContainer>
  );
};

export default TrackPlayer;
